<template>
    <section ref="section" class="slice slice-video slice-video--product" data-testid="video-slice-product" aria-label="video">
        <div class="slice-content">
            <PrismicRichText v-if="isFilled.richText(slice.primary.title)" :field="slice.primary.title" class="slice-title" />
            <PrismicRichText
                v-if="isFilled.richText(slice.primary.description)"
                :field="slice.primary.description"
                class="slice-description slice-video--product__description"
            />
            <div ref="wrapperRef" class="slice-video--product__wrapper slide-in">
                <div ref="innerRef">
                    <MktVideoPlayer ref="videoPlayer" :src="videoSrc" />
                    <div
                        v-if="!videoUnveiled && isFilled.image(slice.primary.coverImage)"
                        ref="coverRef"
                        class="slice-video--product__cover"
                        @click="startVideo()"
                    >
                        <PrismicImage :field="slice.primary.coverImage" class="slice-video--product__cover-image" />
                        <div class="slice-video--product__cover-overlay" />
                        <div class="slice-video--product__cover-content">
                            <svg
                                class="slice-video--product__cover-icon"
                                width="175"
                                height="175"
                                viewBox="0 0 175 175"
                                fill="none"
                                aria-hidden="true"
                            >
                                <circle cx="87.3568" cy="87.0785" r="81.3402" stroke="var(--joy-color-neutral-0)" stroke-width="11.4766" />
                                <path d="M127.285 86.9762L66.8278 121.881L66.8278 52.0712L127.285 86.9762Z" fill="var(--joy-color-neutral-0)" />
                            </svg>
                            <PrismicRichText :field="slice.primary.coverText" class="slice-video--product__cover-title" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, onMounted, ref, useTemplateRef} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {VideoSlice, VideoSliceVideoProduct} from '@prismicio-types';
    import {MktVideoPlayer} from '#components';
    import {useComponentTracking} from '#imports';
    import {useGsap} from '../../../composables/useGsap';
    import {useVideoEmbedPlayer} from '../../../composables/useVideoEmbedPlayer';

    const props = defineProps(getSliceComponentProps<VideoSlice>());
    const slice = computed(() => props.slice as VideoSliceVideoProduct);
    const section = useTemplateRef('section');
    const {getEmbedSrc} = useVideoEmbedPlayer();
    const {ScrollTrigger} = useGsap();

    const videoSrc = computed(() => {
        const src = getEmbedSrc(slice.value.primary.video);
        if (!src) return '';
        return src.includes('?') ? `${src}&rel=0` : `${src}?rel=0`;
    });

    const videoUnveiled = ref(false);
    const wrapperRef = useTemplateRef('wrapperRef');
    const innerRef = useTemplateRef('innerRef');
    const coverRef = useTemplateRef('coverRef');
    const videoPlayer = useTemplateRef('videoPlayer');

    function unveilCover() {
        if (coverRef.value) {
            coverRef.value.style.opacity = '0';
            coverRef.value.addEventListener('transitionend', () => {
                videoUnveiled.value = true;
            });
        }
    }

    function startVideo() {
        unveilCover();
        videoPlayer.value?.play();
    }

    function show() {
        wrapperRef.value?.classList.add('animate');
    }

    function hide() {
        wrapperRef.value?.classList.remove('animate');
        videoPlayer.value?.pause();
    }

    onMounted(() => {
        if (!innerRef.value) return;

        ScrollTrigger.create({
            trigger: wrapperRef.value,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
            onEnter: show,
            onLeave: () => videoPlayer.value?.pause(),
            onLeaveBack: hide,
        });
    });

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-video--product {
        &__description {
            margin-bottom: var(--joy-core-spacing-6);
        }

        &__wrapper {
            position: relative;

            .animate & > * {
                transition-duration: 2s;
            }
        }

        &__cover {
            transition: opacity var(--joy-transition-duration-long) var(--joy-transition-timing-function);
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            border-radius: var(--joy-core-radius-4);
            overflow: hidden;

            &-image,
            &-overlay {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
            }

            &-image {
                object-fit: cover;
            }

            &-overlay {
                opacity: 0.6;
                background: var(--joy-color-ai-gradient-30);
            }

            &-content {
                position: relative;
            }

            &-icon {
                width: 48px;
                height: auto;
                margin-bottom: var(--joy-core-spacing-2);

                @include mq.screen_xs(min) {
                    width: 75px;
                }
                @include mq.screen_sm(min) {
                    width: 128px;
                    margin-bottom: var(--joy-core-spacing-4);
                }
                @include mq.screen_md(min) {
                    width: 175px;
                    margin-bottom: var(--joy-core-spacing-6);
                }
            }

            &-title {
                font-family: var(--joy-font-family-title);
                font-size: var(--joy-font-size-primary-600);
                line-height: var(--joy-line-height-small);

                p {
                    color: var(--joy-color-neutral-0);
                }

                @include mq.screen_xs(min) {
                    font-size: var(--joy-font-size-secondary-200);
                }
                @include mq.screen_sm(min) {
                    font-size: var(--joy-font-size-secondary-400);
                }
                @include mq.screen_md(min) {
                    font-size: var(--joy-font-size-secondary-600);
                }
            }
        }
    }
</style>
